/**
 *  Copyright AlpineReplay Inc, 2022. All rights reserved.
 *  Authors: Tabitha Bleil
 */

import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useHistory } from "react-router-dom"
import {
    StepController,
    Step,
    StepProgress,
    Modal,
    Column,
    Label,
    TitleSection,
    ModalTitle,
    ContentSection,
    Icon,
    Row,
} from "trux"
import { v4 as uuidv4 } from "uuid"
import { parseQuery } from "../../../util/query"
import { PlayerOrFamily } from "./PlayerOrFamily"
import { PlayerDetails } from "./PlayerDetails"
import { InviteFamily } from "./InviteFamily"
import { FinishedStep } from "./FinishedStep"
import { PlayerEmail } from "./PlayerEmail"
import { CollectPlayerEmail } from "./CollectPlayerEmail"
import { AddFamilyMember } from "./AddFamilyMember"
import { WelcomeToTheFamily } from "./WelcomeToTheFamily"
import { LoadingStep } from "./LoadingStep"

import { useGetUserRelationsLazyQuery } from "../../../generated/graphql"

import Lottie from "lottie-react"
import lottieJson from "../assets/tracebot-loading-animation.json"

import "./RosterInvite.css"
import {
    addRelation,
    logIn,
    loadUser,
    emailSwitch,
    getUserInfo,
    logout,
} from "../actions"

const pageNames = { inviteFamily: "" }
export const RosterInvite = (props) => {
    const dispatch = useDispatch()
    const history = useHistory()
    const [userRelations] = useGetUserRelationsLazyQuery({})

    const [players, setPlayers] = useState([])
    const [step, setStep] = useState("setting-up-account")
    const [prev, setPrev] = useState([])
    const [role, setRole] = useState(null)
    const [relats, setRelats] = useState(null)
    const location = useLocation()

    const searchParams = parseQuery(location.search)
    const userIdParam = searchParams
        ? parseInt(searchParams["user_id"], 10)
        : null
    const code = searchParams ? parseInt(searchParams["token"], 10) : null

    const [familyMember, setFamilyMember] = useState(null)
    const [playerPersonalEmail, setPlayerPersonalEmail] = useState(null)

    const [invitedFamilyMembers, setInvitedFamilyMembers] = useState([])

    const [loggedInUser, setLoggedInUser] = useState(null)

    let team = useSelector((state) => state.team.value)

    const logInUser = async () => {
        await logIn(loggedInUser, userIdParam, code)
        let userInfo = await getUserInfo()
        let user = await userInfo

        // force loading screen to show for at least two seconds
        setTimeout(function () {
            if (user) {
                setLoggedInUser(user)
                onSetUpFinished(user)
            }
        }, 2000)
    }

    const addRelationToPlayer = (relationToAdd) => {
        addRelation(loggedInUser.user_id, relats, relationToAdd)
    }

    const updatePlayerEmail = (email) => {
        let emailInput = email
        if (!emailInput) {
            let playerUUID = uuidv4()
            let playerDummyEmail = `dummy_${playerUUID}@traceup.com`
            emailInput = playerDummyEmail
        }
        console.log("else we have an email")
        emailSwitch(
            loggedInUser.user_id,
            { email: emailInput },
            relats,
            familyMember
        )
    }

    const onSetUpFinished = (user) => {
        if (/(?=.*dummy)(?=.*traceup.com)/.test(user.email)) {
            setStep("welcome-family")
        } else {
            userRelations({
                variables: {
                    to_user_id: user.user_id,
                    token: props.token,
                    include_self: true,
                },
            }).then((relations) => {
                const rels = relations?.data?.userRelations
                setRelats(rels)
                console.log("relations", rels)
            })
            setStep("start")
        }
    }

    const onWhoSelected = (e) => {
        setRole(e)
        setPrev((current) => [...current, step])

        if (e === "self") {
            setStep("invite-family")
        } else {
            setStep("details")
        }
    }

    const onBack = () => {
        setStep(prev.pop())
    }

    const onPlayerDetailsDone = (value) => {
        setPrev((current) => [...current, step])
        setFamilyMember(value)
        setStep("player-email")
    }

    const onPlayerEmailSelected = (e) => {
        setPrev((current) => [...current, step])
        if (e === "yes") {
            setStep("collect-player-email")
        } else {
            setStep("invite-family")
        }
    }

    const onPlayerEmailInput = (value) => {
        setPrev((current) => [...current, step])
        setStep("invite-family")
    }

    const onAddFamily = () => {
        setPrev((current) => [...current, step])
        setStep("add-family-member")
    }

    const onFamilyMemberAdded = (e) => {
        setPrev((current) => [...current, step])
        addRelationToPlayer(e)
        setStep("loading")
        setTimeout(function () {
            setStep("invite-sent")
            setTimeout(function () {
                setStep("add-family-member")
                setInvitedFamilyMembers((current) => [...current, e])
            }, 1000)
        }, 1500)
    }

    const onSkip = () => {
        setPrev((current) => [...current, step])
        setStep("finish")
    }

    const onFinished = async () => {
        if (role === "family") {
            logout().then(history.push(`/${team?.name}/family`))
        } else {
            history.push(`/${team?.name}/family`)
        }
    }

    return (
        <Modal open className="RosterInvite">
            <StepController step={step}>
                <Step name="start">
                    <PlayerOrFamily
                        loggedInUser={loggedInUser}
                        onWhoSelected={onWhoSelected}
                    />
                </Step>
                <Step name="details">
                    <StepProgress steps={5} index={1} />
                    <PlayerDetails
                        loggedInUser={loggedInUser}
                        role={role}
                        onDone={onPlayerDetailsDone}
                        onBackClick={onBack}
                    />
                </Step>
                <Step name="player-email">
                    <StepProgress steps={5} index={2} />
                    <PlayerEmail
                        loggedInUser={loggedInUser}
                        updatePlayerEmail={updatePlayerEmail}
                        onSelected={onPlayerEmailSelected}
                        onBackClick={onBack}
                    />
                </Step>
                <Step name="collect-player-email">
                    <StepProgress steps={5} index={2} />
                    <CollectPlayerEmail
                        onDone={onPlayerEmailInput}
                        updatePlayerEmail={updatePlayerEmail}
                        onBackClick={onBack}
                    />
                </Step>
                <Step name="invite-family">
                    <StepProgress steps={5} index={3} />
                    <InviteFamily
                        loggedInUser={loggedInUser}
                        onAddFamily={onAddFamily}
                        onSkip={onSkip}
                    />
                </Step>
                <Step name="add-family-member">
                    <StepProgress steps={5} index={4} />
                    <AddFamilyMember
                        invitedFamilyMembers={invitedFamilyMembers}
                        onDone={onFamilyMemberAdded}
                        onBackClick={onBack}
                        onSkip={onSkip}
                    />
                </Step>
                <Step name="finish">
                    <StepProgress steps={5} index={5} />
                    <FinishedStep
                        loggedInUser={loggedInUser}
                        onDone={onFinished}
                        onBackClick={onBack}
                    />
                </Step>
                <Step name="welcome-family">
                    <WelcomeToTheFamily
                        loggedInUser={loggedInUser}
                        onAddFamily={onAddFamily}
                        onSkip={onSkip}
                    />
                </Step>

                <Step name="loading">
                    <>
                        <TitleSection>
                            <ModalTitle
                                content={`Family Member ${
                                    invitedFamilyMembers.length + 1
                                }`}
                            />
                        </TitleSection>
                        <ContentSection>
                            <Column center span="12" className="loading">
                                <Lottie
                                    loop
                                    play
                                    animationData={lottieJson}
                                    style={{ width: "auto", height: 40 }}
                                />
                                <Label>Sending invite...</Label>
                            </Column>
                        </ContentSection>
                    </>
                </Step>
                <Step name="invite-sent">
                    <>
                        <TitleSection>
                            <ModalTitle
                                content={`Family Member ${
                                    invitedFamilyMembers.length + 1
                                }`}
                            />
                        </TitleSection>
                        <ContentSection>
                            <Column center span="12" className="loading">
                                <Row height="40px">
                                    <Icon type={Icon.Type.Check} />
                                </Row>

                                <Label>Invite sent!</Label>
                            </Column>
                        </ContentSection>
                    </>
                </Step>
                <Step name="setting-up-account">
                    <LoadingStep
                        loggedInUser={loggedInUser}
                        logInUser={logInUser}
                        isLoggedIn={props.isLoggedIn}
                        token={props.token}
                        onDone={onSetUpFinished}
                    />
                </Step>
            </StepController>
        </Modal>
    )
}
